import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import isEqual from 'lodash/isEqual';
import { parse } from 'qs';

import { Button } from '@/common/components/button';
import { Phototron } from '@/common/components/phototron';
import { redirect } from '@/common/utils';
import { GuardProps, unauthenticatedRoutesSearch } from '@/core/navigation';
import { getLoginPageUrl } from '@/core/services/authentication';
import { VerifyProps } from '../../services/verify.guard';
import { style } from './styles';

type Props = GuardProps<VerifyProps>;
type LocationSearch = unauthenticatedRoutesSearch['LOGOUT'];

export const LogoutPage: FC<Props> = ({ guardProps }) => {
  const [t] = useTranslation();
  const handleRedirect = useCallback(() => redirect(getLoginPageUrl()), []);
  const location = useLocation<LocationSearch>();
  const prevSearch = useRef<LocationSearch | undefined>(undefined);
  const history = useHistory();

  useEffect(() => {
    const state = parse(location.search, { ignoreQueryPrefix: true }) as LocationSearch;
    if (prevSearch.current === undefined || (prevSearch.current !== undefined && !isEqual(prevSearch.current, location.state))) {
      history.replace({ state, pathname: location.pathname });
    }

    prevSearch.current = state;
  }, [location]);

  const renderContent = useMemo(() => {
    const status = guardProps?.reason || location.state?.reason;
    switch (status) {
      case 'TOKEN': {
        return (
          <>
            <h2 className='title'>{t('page.logout.tokenTitle')}</h2>
            <p className='description'>{t('page.logout.tokenDescription')}</p>
          </>
        );
      }

      case 'USER_PROFILE': {
        return (
          <>
            <h2 className='title'>{t('page.logout.profileTitle')}</h2>
            <p className='description'>{t('page.logout.profileDescription')}</p>
          </>
        );
      }

      case 'COMMON_ERROR': {
        return (
          <>
            <h2 className='title'>{t('page.logout.commonTitle')}</h2>
            <p className='description'>{t('page.logout.commonErrorDescription')}</p>
          </>
        );
      }

      default: {
        return (
          <>
            <h2 className='title'>{t('page.logout.commonTitle')}</h2>
            <p className='description'>{t('page.logout.commonDescription')}</p>
          </>
        );
      }
    }
  }, [location, guardProps]);

  return (
    <div css={style}>
      <Phototron status='error' />
      {renderContent}
      <Button onClick={handleRedirect} className='button'>
        {t('button.backToLogin')}
      </Button>
    </div>
  );
};
